import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CoinPile } from '@/shared/components/reward/coin-pile';
import { DailyDeal } from '@/shared/components/reward/daily-deal-card';
import { formatCountdown } from '@/shared/components/reward/coin-wallet';
import { useCoinWallet } from '@/shared/components/reward/use-coin-wallet';
import '@/shared/components/reward/coin-wallet.css';

export interface CoinRushProps {
  /** @deprecated the server uses the authenticated user; kept for callers. */
  userID?: string;
  onClose?: () => void;
  /** @deprecated watch-ad spins were removed (no server verification). Ignored. */
  showAdButton?: boolean;
  /** @deprecated ignored */
  showTitleInWheel?: boolean;
}

// Literal route so this file does not depend on the coins-info module graph.
const WALLET_PATH = '/coins-info';

/**
 * Home entry point for the Daily deal: a floating pill that shows whether the
 * deal is ready or how long until it is, and a dialog with the claim card.
 * Opens only on request; never interrupts the home screen. Account-only: renders nothing for guests.
 */
export const CoinRush: FC<CoinRushProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const wallet = useCoinWallet();
  const [open, setOpen] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const entryRef = useRef<HTMLButtonElement>(null);

  const ready = wallet.status?.kind === 'ready';
  const cooldown = wallet.status?.kind === 'cooldown' ? wallet.status : null;



  const close = useCallback(() => {
    setOpen(false);
    onClose?.();
    window.requestAnimationFrame(() => entryRef.current?.focus());
  }, [onClose]);
  const closeLatest = useRef(close);
  closeLatest.current = close;

  // Focus + Escape only when the dialog opens, not on every countdown tick.
  useEffect(() => {
    if (!open) return undefined;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLatest.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!wallet.enabled) return null;

  const sub = ready
    ? `${wallet.wallet?.rewardAmount ?? ''} coins ready`.trim()
    : cooldown
      ? `Next in ${formatCountdown(cooldown.remainingMs)}`
      : wallet.isError
        ? 'Tap to retry'
        : 'Checking…';

  return (
    <>
      {!open && (
        <button
          ref={entryRef}
          type="button"
          className={`cw-fab ${ready ? 'cw-fab--ready' : ''}`}
          onClick={e => {
            returnFocus.current = e.currentTarget;
            setOpen(true);
          }}
          aria-haspopup="dialog"
        >
          <CoinPile size="md" />
          {ready && <span className="cw-fab__dot" aria-hidden="true" />}
          <span>
            Daily deal
            <small>{sub}</small>
          </span>
        </button>
      )}

      {open && (
        <>
          <div className="cw-overlay" onClick={close} aria-hidden="true" />
          <div
            className="cw-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Daily deal"
          >
            <div className="cw-dialog__bar">
              <button
                type="button"
                className="cw-dialog__link"
                onClick={() => {
                  close();
                  navigate(WALLET_PATH);
                }}
              >
                Open wallet
              </button>
              <button
                type="button"
                ref={closeRef}
                className="cw-dialog__close"
                onClick={close}
                aria-label="Close daily deal"
              >
                ×
              </button>
            </div>
            <DailyDeal compact />
          </div>
        </>
      )}
    </>
  );
};
